/**
 * assets/klaviyo-member-register.js
 *
 * Klaviyo 会员弹窗提交 → Shokz Member 注册
 *
 * ## 背景
 * - 运营在 Klaviyo 配置全站（或指定场景）会员注册弹窗：
 *   - 手机端：signup_member_pop-up_mobile（Form ID: TvYwUr）
 *   - 桌面端：signup_member_pop_up_desktop（Form ID: UJGnfz）
 * - 主题侧不负责「何时自动弹出」（由 Klaviyo Targeting / App Embed 控制），
 *   只负责：监听到上述表单 submit 后，取出邮箱并调用会员注册接口。
 *
 * ## 本脚本做什么
 * 1. 监听 window 上的 `klaviyoForms` 自定义事件（Klaviyo Onsite 派发）。
 * 2. 仅处理 Form ID 为 TvYwUr / UJGnfz 的 `submit`。
 * 3. 从 `detail.metaData`（必要时 DOM 兜底）解析邮箱。
 * 4. 调用 `ShokzMember.registerShopifyMember(email)` → POST us-shopify-register。
 * 5. 空邮箱、同会话同表单+邮箱去重时跳过，避免多步表单重复打接口。
 *
 * ## 引入方式（layout/theme.liquid）
 * ```liquid
 * <script type="module" src="{{ 'klaviyo-member-register.js' | asset_url }}"></script>
 * ```
 * 使用 `type="module"`（自带 defer 语义），以便 `import ShokzMember`。
 * 依赖 `snippets/import-map.liquid` 中的 `@theme/shokz-member` 映射。
 *
 * ## 控制台调试（可选，默认已注释）
 * 取消注释下方 `window.klaviyoProbe` 后可用：
 * - klaviyoProbe.openMobile()  → 打开 TvYwUr
 * - klaviyoProbe.openDesktop() → 打开 UJGnfz
 * - klaviyoProbe.close()       → 关闭当前表单
 *
 * ## 维护注意
 * - 更换 Form ID：改下方 FORM_MOBILE / FORM_DESKTOP，并同步运营 / dataLayer 埋点。
 * - 埋点 module：`Pop_Up_member`（snippets/dataLayer.liquid），与本脚本职责分离。
 * - Console logs are English and commented out by default; uncomment when debugging.
 * - Log prefix: `[Klaviyo Member Register]`.
 *
 * @module klaviyo-member-register
 */

import ShokzMember from '@theme/shokz-member';

window._klOnsite = window._klOnsite || [];

/** @type {RegExp} 简单邮箱格式校验 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 本会话内已发起过注册的「formId:email」集合，防止重复提交。
 * @type {Set<string>}
 */
const recentRegisterKeys = new Set();

/** 手机端 Form ID：signup_member_pop-up_mobile */
const FORM_MOBILE = 'TvYwUr';

/** 桌面端 Form ID：signup_member_pop_up_desktop */
const FORM_DESKTOP = 'UJGnfz';

/** Console log prefix */
const LOG_PREFIX = '[Klaviyo Member Register]';

/**
 * 从 Klaviyo submit 的 metaData 中提取邮箱。
 * @param {Record<string, unknown>|null|undefined} meta
 * @returns {string}
 */
function extractEmailFromMeta(meta) {
  if (!meta || typeof meta !== 'object') return '';
  const direct = meta.email || meta.$email || meta.Email || meta.EMAIL || '';
  if (typeof direct === 'string' && EMAIL_RE.test(direct.trim())) {
    return direct.trim();
  }
  for (const value of Object.values(meta)) {
    if (typeof value === 'string' && EMAIL_RE.test(value.trim())) {
      return value.trim();
    }
  }
  return '';
}

/**
 * 从页面上对应 formId 的 Klaviyo 表单 DOM 兜底读邮箱（meta 无值时）。
 * @param {string} formId
 * @returns {string}
 */
function extractEmailFromDom(formId) {
  const root =
    document.querySelector(`form[data-testid="klaviyo-form-${formId}"]`) ||
    document.querySelector(`.klaviyo-form-${formId}`) ||
    document.querySelector(`[data-testid="klaviyo-form-${formId}"]`);
  if (!root) return '';
  const input =
    root.querySelector('input[type="email"]') ||
    root.querySelector('input[name*="email" i]') ||
    root.querySelector('input[autocomplete="email"]');
  const value = (input && input.value) || '';
  return EMAIL_RE.test(value.trim()) ? value.trim() : '';
}

/**
 * Human-readable form label (for logs).
 * @param {string} formId
 * @returns {string}
 */
function formLabel(formId) {
  if (formId === FORM_MOBILE) return 'mobile signup_member_pop-up_mobile';
  if (formId === FORM_DESKTOP) return 'desktop signup_member_pop_up_desktop';
  return formId || 'unknown form';
}

/**
 * 手动打开 / 关闭目标表单（联调、验收用；默认注释，需要时取消注释）。
 * @typedef {object} KlaviyoProbe
 * @property {() => void} openMobile
 * @property {() => void} openDesktop
 * @property {() => void} close
 */

// /** @type {KlaviyoProbe} */
// window.klaviyoProbe = {
//   openMobile() {
//     window._klOnsite.push(['openForm', FORM_MOBILE]);
//     // console.log(`${LOG_PREFIX} open mobile form`, { formId: FORM_MOBILE });
//   },
//   openDesktop() {
//     window._klOnsite.push(['openForm', FORM_DESKTOP]);
//     // console.log(`${LOG_PREFIX} open desktop form`, { formId: FORM_DESKTOP });
//   },
//   close() {
//     window._klOnsite.push(['closeForm']);
//     // console.log(`${LOG_PREFIX} close current Klaviyo form`);
//   },
// };

// console.info(
//   `${LOG_PREFIX} ready — listening mobile ${FORM_MOBILE} / desktop ${FORM_DESKTOP}. Use klaviyoProbe.openMobile() / openDesktop() / close()`
// );

/**
 * 处理 Klaviyo 表单事件：目标表单 submit → 取邮箱 → 会员注册。
 * @param {CustomEvent} e
 */
async function onKlaviyoForms(e) {
  const detail = e.detail || {};
  const eventType = detail.type;

  if (eventType !== 'submit') {
    if (eventType === 'open' || eventType === 'close') {
      const fid = detail.formId || detail.formID;
      if (fid === FORM_MOBILE || fid === FORM_DESKTOP) {
        // console.log(`${LOG_PREFIX} form event`, {
        //   type: eventType,
        //   form: formLabel(fid),
        //   formId: fid,
        // });
      }
    }
    return;
  }

  const formId = detail.formId || detail.formID;
  if (formId !== FORM_MOBILE && formId !== FORM_DESKTOP) return;

  // console.log(`${LOG_PREFIX} 1) target form submit`, {
  //   form: formLabel(formId),
  //   formId,
  //   type: eventType,
  //   detail,
  // });

  const meta = detail.metaData || {};
  const emailFromMeta = extractEmailFromMeta(meta);
  const emailFromDom = emailFromMeta ? '' : extractEmailFromDom(formId);
  const email = emailFromMeta || emailFromDom || '';
  // const emailSource = emailFromMeta
  //   ? 'metaData'
  //   : emailFromDom
  //     ? 'DOM fallback'
  //     : 'not found';

  // console.log(`${LOG_PREFIX} 2) parse email`, {
  //   email: email || '(empty)',
  //   source: emailSource,
  //   metaKeys: Object.keys(meta),
  //   metaData: meta,
  // });

  if (!email) {
    // console.warn(
    //   `${LOG_PREFIX} 3) skip register — empty email (likely a later multi-step submit)`,
    //   { formId, form: formLabel(formId) }
    // );
    return;
  }

  const dedupeKey = `${formId}:${email.toLowerCase()}`;
  if (recentRegisterKeys.has(dedupeKey)) {
    // console.warn(`${LOG_PREFIX} 3) skip register — already handled this form+email in session`, {
    //   dedupeKey,
    //   email,
    //   form: formLabel(formId),
    // });
    return;
  }
  recentRegisterKeys.add(dedupeKey);

  // console.log(`${LOG_PREFIX} 3) calling registerShopifyMember`, {
  //   email,
  //   form: formLabel(formId),
  //   pageUrl: window.location.href,
  // });

  try {
    // const res = await ShokzMember.registerShopifyMember(email);
    // const ok = res && (res.code === 0 || res.code === '0');
    // if (ok) {
    //   console.log(`${LOG_PREFIX} 4) register success`, {
    //     email,
    //     form: formLabel(formId),
    //     response: res,
    //   });
    // } else {
    //   console.warn(`${LOG_PREFIX} 4) register returned non-success`, {
    //     email,
    //     form: formLabel(formId),
    //     response: res,
    //   });
    // }
    const res = await ShokzMember.registerShopifyMember(email);
    ShokzMember.pushMembershipSignup({
      contentType: 'popup',
      contentName: 'Pop_Up',
      payload: res,
    });
  } catch (err) {
    recentRegisterKeys.delete(dedupeKey);
    ShokzMember.pushMembershipSignup({
      contentType: 'popup',
      contentName: 'Pop_Up',
      payload: { code: -1, member_status: 'fail' },
    });
    // console.error(`${LOG_PREFIX} 4) register request failed (dedupe cleared, can retry)`, {
    //   email,
    //   form: formLabel(formId),
    //   error: err,
    // });
  }
}

window.addEventListener('klaviyoForms', onKlaviyoForms);
